import { useEffect, useRef } from 'react';
import { FieldShell, useControlMeta } from '@scientist/dynamic-form-renderer';

// Interactive ChemDoodle structure sketcher, registered with the React dynamic
// form renderer as the `chem_doodle` widget (alpaca_importer.rb maps the legacy
// Alpaca `chemDoodle` field to `markup` + config.widget: "chem_doodle").
//
// The value is an MDL moleblock string stored under the field key — identical to
// the legacy Alpaca/Stimulus implementation
// (app/javascript/controllers/dynamic_form_chemdoodle.js), so values round-trip
// between the two renderers untouched.
//
// ChemDoodle ships as a global from static assets
// (/chemdoodle/ChemDoodleWeb-11.js, loaded synchronously by
// shared/_chemdoodle_web_components, so the global is ready before React
// mounts). The sketcher is interactive in every mode except "readonly"
// (matching the legacy Alpaca form, which has no read-only mode). When the
// field is in readonly mode, is disabled, or the global is missing, we render
// read-only text so the value is never lost.
//
// DOM ownership: ChemDoodle's SketcherCanvas constructor REPLACES the target
// canvas in place with its own generated DOM (toolbar + canvas). React must not
// own that subtree, or a re-render (validateOnMount, or any store change via
// useSyncExternalStore) reconciles it away and the sketcher silently vanishes.
// So we render an EMPTY React-owned host <div> and build/seed/teardown the
// canvas imperatively inside it — React has no children to diff and leaves
// ChemDoodle's DOM alone. Same escape hatch the Stimulus controller relied on.

const SKETCHER_WIDTH = 600;
const SKETCHER_HEIGHT = 300;

function readOnly(meta, value, field, path, errors) {
  const text = typeof value === 'string' ? value : value === null || value === undefined ? '' : JSON.stringify(value);
  return (
    <FieldShell field={field} path={path} errors={errors}>
      {text ? (
        <pre id={meta.id} className="form-control-plaintext mb-0 text-break" style={{ whiteSpace: 'pre-wrap' }}>
          {text}
        </pre>
      ) : (
        <div id={meta.id} className="form-text fst-italic">
          No structure drawn.
        </div>
      )}
    </FieldShell>
  );
}

// Build the ChemDoodle sketcher inside `host`, seed it, and wire write-back.
// Returns a teardown that empties the host.
function buildSketcher(host, canvasId, ChemDoodle, initial, writeRef) {
  const canvas = document.createElement('canvas');
  canvas.id = canvasId;
  host.appendChild(canvas);

  const sketcher = new ChemDoodle.SketcherCanvas(canvasId, SKETCHER_WIDTH, SKETCHER_HEIGHT, {
    oneMolecule: true,
    useServices: false,
  });

  // Hide file-system toolbar buttons that don't apply to embedded form use.
  ['buttonSave', 'buttonOpen', 'buttonTemplate'].forEach((name) => {
    const button = sketcher.toolbarManager?.[name];
    if (button?.getElement) button.getElement().style.display = 'none';
  });

  // Load any existing moleblock into the sketcher.
  if (typeof initial === 'string' && initial.trim() !== '') {
    try {
      sketcher.loadMolecule(ChemDoodle.readMOL(initial));
    } catch (err) {
      // Malformed/legacy moleblock — start empty rather than crash the form.
      // eslint-disable-next-line no-console
      console.warn('[ChemDoodleWidget] could not parse stored moleblock', err);
    }
  }

  // Sync every edit back into the form store as a moleblock string.
  const original = sketcher.checksOnAction;
  sketcher.checksOnAction = function checksOnAction(force) {
    if (typeof original === 'function') original.call(this, force);
    const { setFieldValue, path: currentPath } = writeRef.current;
    setFieldValue(currentPath, ChemDoodle.writeMOL(sketcher.getMolecule()));
  };

  return () => {
    host.replaceChildren();
  };
}

export default function ChemDoodleWidget({ field, path, value, errors }) {
  const meta = useControlMeta(field, path, errors);
  const hostRef = useRef(null);

  // Keep the latest write target without re-initializing the sketcher: the setup
  // effect runs once, but setFieldValue/path identities can change between
  // renders.
  const writeRef = useRef({ setFieldValue: meta.setFieldValue, path });
  writeRef.current = { setFieldValue: meta.setFieldValue, path };

  // Seed once from the initial value; later value changes come from the sketcher
  // itself, so we deliberately do not re-seed on every render.
  const initialValueRef = useRef(value);

  const interactive =
    meta.mode !== 'readonly' && !meta.disabled && typeof window !== 'undefined' && !!window.ChemDoodle;

  const canvasId = `${meta.id}-chemdoodle`;

  useEffect(() => {
    if (!interactive) return undefined;
    const host = hostRef.current;
    if (!host) return undefined;

    const teardown = buildSketcher(host, canvasId, window.ChemDoodle, initialValueRef.current, writeRef);

    return teardown;
    // canvasId is derived from path; re-running on interactive/canvasId change is
    // the intended teardown/setup boundary.
  }, [interactive, canvasId]);

  if (!interactive) {
    return readOnly(meta, value, field, path, errors);
  }

  return (
    <FieldShell field={field} path={path} errors={errors}>
      <div ref={hostRef} aria-describedby={meta.describedBy} />
    </FieldShell>
  );
}
